import React, { useState } from 'react';
import { FiX, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiClock, FiMonitor } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { toast } from 'react-toastify';

const ResetTestSessionModal = ({ isVisible, onClose, student, testDetails, onSuccess }) => {
  const [resetting, setResetting] = useState(false);

  if (!isVisible) return null;

  const handleResetSession = async () => {
    if (!student?._id) {
      toast.error('Session ID not found');
      return;
    }

    setResetting(true);
    try {
      await userRequest.patch(`/test-session/${student._id}/reset-user-agent`);
      
      toast.success('Test session reset successfully! Student can now continue on a different device.');
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error resetting test session:', error);
      toast.error('Failed to reset test session. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'not_started':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheckCircle className="w-4 h-4" />;
      case 'in_progress':
        return <FiClock className="w-4 h-4" />;
      default:
        return <FiMonitor className="w-4 h-4" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex justify-between items-start">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <FiRefreshCw className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-slate-800 mb-1">
                  Reset Test Session
                </h2>
                <p className="text-sm text-slate-600">
                  Reset user agent and IP to allow student to continue on a different device
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Student Information */}
          <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <h3 className="font-semibold text-slate-800 mb-3">Student Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Student Name</label>
                <p className="text-slate-800 font-medium">
                  {student?.child?.firstname} {student?.child?.lastname}
                </p>
              </div>
              {/*<div>
                <label className="text-sm font-medium text-slate-600">Student ID</label>
                <p className="text-slate-800 font-mono text-sm">{student?.child?._id}</p>
              </div>*/}
            </div>
          </div>

          {/* Test Information */}
          {testDetails && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h3 className="font-semibold text-slate-800 mb-3">Test Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Test Title</label>
                  <p className="text-slate-800 font-medium">{testDetails.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Subject</label>
                  <p className="text-slate-800">{testDetails.subject?.subject_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Class</label>
                  <p className="text-slate-800">{testDetails.class?.class_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Duration</label>
                  <p className="text-slate-800">{testDetails.duration} minutes</p>
                </div>
              </div>
            </div>
          )}

          {/* Session Information */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <h3 className="font-semibold text-slate-800 mb-3">Current Session Details</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Session Status</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(student?.status)}`}>
                  {getStatusIcon(student?.status)}
                  <span className="ml-1">{student?.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}</span>
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Started At</label>
                  <p className="text-slate-800 text-sm">{formatDate(student?.start_time)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Due At</label>
                  <p className="text-slate-800 text-sm">{formatDate(student?.due_time)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Progress</label>
                  <p className="text-slate-800 text-sm">
                    {student?.answered_questions || 0}/{student?.total_questions || 0} questions
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Current Score</label>
                  <p className="text-slate-800 text-sm">
                    {student?.current_score || 0}/{student?.total_score || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Device Information 
          {student?.device_info?.fingerprint && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
              <h3 className="font-semibold text-slate-800 mb-3">Current Device Information</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium text-slate-600">IP Address</label>
                  <p className="text-slate-800 font-mono text-sm">{student.device_info.fingerprint.ip}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">User Agent</label>
                  <p className="text-slate-800 text-xs break-all">{student.device_info.fingerprint.user_agent}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Device Fingerprint Created</label>
                  <p className="text-slate-800 text-sm">{formatDate(student.device_info.fingerprint.created_at)}</p>
                </div>
              </div>
            </div>
          )}*/}

          {/* Warning Message */}
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <FiAlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-800 mb-2">Important Notice</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• This action will reset the student tset device for this test session</li>
                  <li>• The student will be able to continue the test on a different device</li>
                  <li>• This action cannot be undone</li>
                  <li>• Only use this when the student needs to switch devices due to technical issues</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-slate-50">
          <div className="text-sm text-slate-500">
            Session ID: <span className="font-mono">{student?._id}</span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={resetting}
              className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:bg-slate-400 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResetSession}
              disabled={resetting}
              className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg transition-colors flex items-center"
            >
              {resetting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Resetting...
                </>
              ) : (
                <>
                  <FiRefreshCw className="w-4 h-4 mr-2" />
                  Reset Session
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetTestSessionModal;
